import type { Command, CommandMap } from './command.types'

import type { UseHotkeyOptions } from '@tanstack/react-hotkeys'
import { useHotkeys } from '@tanstack/react-hotkeys'

import { isCommandEnabled, isCommandVisible } from './command.utils'

type Options<Context> = {
  scopeEnabled?: boolean
  /** Callback to run after command executes */
  onExecuted?: (command: Command<Context>, context: Context) => void
}

export function useCommandHotkeys<Context>(
  commands: CommandMap<Context>,
  context: Context | null,
  options: Options<Context>,
) {
  const isScopeEnabled = options.scopeEnabled ?? true

  useHotkeys(
    Object.values(commands).flatMap((command) =>
      (command.shortcuts ?? []).map((hotkey) => {
        const isEnabled =
          isScopeEnabled &&
          context !== null &&
          isCommandVisible(command, context) &&
          isCommandEnabled(command, context) &&
          (command.isShortcutEnabled?.(context) ?? true)

        return {
          hotkey,
          callback: (event: KeyboardEvent) => {
            if (!context) return

            event.preventDefault()

            command.execute(context)

            options.onExecuted?.(command, context)
          },
          options: {
            enabled: isEnabled,
            requireReset: command.isHotkeyRepeatable !== true,
          } satisfies UseHotkeyOptions,
        }
      }),
    ),
  )
}
